/* eslint-disable */
import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import { OpenInNew, CheckCircle, Build, BugReport, Warning } from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import type { SRDetails, Ticket, StabilizationBug, StabilizationStats } from '../../types/release';

interface AnalyticsProps {
  filteredCustomerValue: SRDetails[];
  filteredRDActivities: Ticket[];
  filteredSL3Tickets: Ticket[];
  filteredStabilizationBugs: StabilizationBug[];
  filteredStabilizationStats?: StabilizationStats;
  release: string;
  codeFreezeDate?: string;
}

const JIRA_BASE_URL = 'https://outsight.atlassian.net';

// Build Jira URL from ticket IDs
const buildJiraUrl = (ticketIds: string[]): string | null => {
  if (ticketIds.length === 0) return null;
  const jql = `key IN (${ticketIds.join(', ')})`;
  return `${JIRA_BASE_URL}/issues/?jql=${encodeURIComponent(jql)}`;
};

export const Analytics: React.FC<AnalyticsProps> = ({
  filteredCustomerValue,
  filteredRDActivities,
  filteredSL3Tickets,
  filteredStabilizationBugs,
  filteredStabilizationStats,
  release,
  codeFreezeDate,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Theme-aware colors for charts
  const chartColors = useMemo(() => {
    if (isDark) {
      // Dark mode: softer, more muted colors
      return [
        '#90caf9', // Light blue
        '#81c784', // Light green
        '#ffb74d', // Light orange
        '#f48fb1', // Light pink
        '#ba68c8', // Light purple
        '#4dd0e1', // Light cyan
      ];
    } else {
      // Light mode: vibrant colors
      return ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];
    }
  }, [isDark]);

  // Calculate status distribution from filtered tickets
  const statusDistribution = useMemo(() => {
    const allTickets = [
      ...filteredRDActivities,
      ...filteredSL3Tickets,
      ...filteredCustomerValue.flatMap(sr => sr.engineering_tickets || []),
    ];

    const statusCounts: Record<string, number> = {};
    allTickets.forEach(ticket => {
      const status = ticket.status || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    return Object.entries(statusCounts).map(([name, value]) => ({
      name,
      value,
    }));
  }, [filteredRDActivities, filteredSL3Tickets, filteredCustomerValue]);

  // Calculate category distribution from filtered tickets
  const categoryDistribution = useMemo(() => {
    return [
      {
        name: 'Customer Value',
        value: filteredCustomerValue.reduce((sum, sr) => sum + (sr.engineering_tickets?.length || 0), 0),
      },
      {
        name: 'R&D Activities',
        value: filteredRDActivities.length,
      },
      {
        name: 'SL3 Tickets',
        value: filteredSL3Tickets.length,
      },
    ];
  }, [filteredCustomerValue, filteredRDActivities, filteredSL3Tickets]);

  // Calculate validation issues summary from filtered tickets
  const validationIssuesSummary = useMemo(() => {
    const allTickets = [
      ...filteredRDActivities,
      ...filteredSL3Tickets,
      ...filteredCustomerValue.flatMap(sr => sr.engineering_tickets || []),
    ];

    const issueCounts: Record<string, number> = {
      error: 0,
      warning: 0,
      info: 0,
    };

    allTickets.forEach(ticket => {
      ticket.validation_issues.forEach(issue => {
        issueCounts[issue.severity] = (issueCounts[issue.severity] || 0) + 1;
      });
    });

    return Object.entries(issueCounts).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
    }));
  }, [filteredRDActivities, filteredSL3Tickets, filteredCustomerValue]);

  // Stabilization bugs by product scope from filtered bugs
  const stabilizationBugsByScope = useMemo(() => {
    const scopeCounts: Record<string, number> = {};
    filteredStabilizationBugs.forEach(bug => {
      const scope = bug.product_scope || 'Unknown';
      scopeCounts[scope] = (scopeCounts[scope] || 0) + 1;
    });
    
    return Object.entries(scopeCounts).map(([name, value]) => ({
      name,
      value,
    }));
  }, [filteredStabilizationBugs]);

  // Stabilization bugs timeline from filtered bugs
  const stabilizationBugsTimeline = useMemo(() => {
    if (!filteredStabilizationStats?.timeline) {
      // Fallback: compute from filtered bugs if stats not provided
      const dateCounts: Record<string, number> = {};
      filteredStabilizationBugs.forEach(bug => {
        if (bug.created_date) {
          const date = new Date(bug.created_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          dateCounts[date] = (dateCounts[date] || 0) + 1;
        }
      });
      return Object.entries(dateCounts)
        .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
        .map(([date, count]) => ({ date, count }));
    }
    
    const timeline = filteredStabilizationStats.timeline;
    if (!Array.isArray(timeline)) return [];
    return timeline.map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      count: item.count,
    }));
  }, [filteredStabilizationBugs, filteredStabilizationStats]);

  // Calculate totals from filtered tickets
  const totalTickets = useMemo(() => {
    return filteredCustomerValue.reduce((sum, sr) => sum + (sr.engineering_tickets?.length || 0), 0) +
           filteredRDActivities.length +
           filteredSL3Tickets.length;
  }, [filteredCustomerValue, filteredRDActivities, filteredSL3Tickets]);

  const lateTicketsCount = useMemo(() => {
    const allTickets = [
      ...filteredRDActivities,
      ...filteredSL3Tickets,
      ...filteredCustomerValue.flatMap(sr => sr.engineering_tickets || []),
    ];
    return allTickets.filter(ticket => ticket.is_late).length;
  }, [filteredRDActivities, filteredSL3Tickets, filteredCustomerValue]);

  const validationIssuesCount = useMemo(() => {
    const allTickets = [
      ...filteredRDActivities,
      ...filteredSL3Tickets,
      ...filteredCustomerValue.flatMap(sr => sr.engineering_tickets || []),
    ];
    return allTickets.reduce((sum, ticket) => sum + ticket.validation_issues.length, 0);
  }, [filteredRDActivities, filteredSL3Tickets, filteredCustomerValue]);

  // Memoized Jira URLs for late tickets and compliance issues
  const lateTicketsJiraUrl = useMemo(() => {
    const allTickets = [
      ...filteredRDActivities,
      ...filteredSL3Tickets,
      ...filteredCustomerValue.flatMap(sr => sr.engineering_tickets || []),
    ];
    const ids = allTickets.filter(ticket => ticket.is_late).map(ticket => ticket.id);
    return buildJiraUrl(ids);
  }, [filteredRDActivities, filteredSL3Tickets, filteredCustomerValue]);

  const complianceIssuesJiraUrl = useMemo(() => {
    const allTickets = [
      ...filteredRDActivities,
      ...filteredSL3Tickets,
      ...filteredCustomerValue.flatMap(sr => sr.engineering_tickets || []),
    ];
    const ids = allTickets.filter(ticket => ticket.validation_issues.length > 0).map(ticket => ticket.id);
    return buildJiraUrl(ids);
  }, [filteredRDActivities, filteredSL3Tickets, filteredCustomerValue]);

  // Build fallback URLs if ticket IDs are not available
  const releaseVersion = `Shift-${release}`;
  const lateTicketsFallbackUrl = lateTicketsCount > 0
    ? `${JIRA_BASE_URL}/issues/?jql=${encodeURIComponent(`project = AL AND fixVersion = "${releaseVersion}" AND status != Done AND status != "QA Acceptance" AND status != Integrated AND status != "In Validation"`)}`
    : null;
  
  const complianceIssuesFallbackUrl = validationIssuesCount > 0
    ? `${JIRA_BASE_URL}/issues/?jql=${encodeURIComponent(`project = AL AND fixVersion = "${releaseVersion}"`)}`
    : null;

  const finalLateTicketsUrl = lateTicketsJiraUrl || lateTicketsFallbackUrl;
  const finalComplianceIssuesUrl = complianceIssuesJiraUrl || complianceIssuesFallbackUrl;

  return (
    <Box sx={{ mb: 5 }}>
      <Typography variant="h6" gutterBottom>
        Analytics Dashboard
      </Typography>

      <Grid
        container
        spacing={3}
        sx={{
          mt: 1.5,
          mx: 0,
          width: '100%',
        }}
      >
        {/* Summary Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: 'text.secondary' }}>
                  <CheckCircle />
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {totalTickets}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Tickets
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ color: 'success.main' }}>
                      <CheckCircle />
                    </Box>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                      {filteredCustomerValue.reduce((sum, sr) => sum + (sr.engineering_tickets?.length || 0), 0)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Customer Value Features
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: 'primary.main' }}>
                  <Build />
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {filteredRDActivities.length}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                R&D Activities
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: 'error.main' }}>
                  <BugReport />
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {filteredSL3Tickets.length}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                SL3 Tickets
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ color: lateTicketsCount > 0 ? 'error.main' : 'text.secondary' }}>
                      <Warning />
                    </Box>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: lateTicketsCount > 0 ? 'error.main' : 'text.secondary' }}>
                      {lateTicketsCount}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Late Tickets
                  </Typography>
                </Box>
                {finalLateTicketsUrl && (
                  <Tooltip title="View in Jira">
                    <IconButton
                      size="small"
                      component="a"
                      href={finalLateTicketsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        ml: 'auto',
                        flexShrink: 0,
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                      aria-label="View Late Tickets in Jira"
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ color: validationIssuesCount > 0 ? 'warning.main' : 'text.secondary' }}>
                      <Warning />
                    </Box>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: validationIssuesCount > 0 ? 'warning.main' : 'text.secondary' }}>
                      {validationIssuesCount}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Compliance Issues
                  </Typography>
                </Box>
                {finalComplianceIssuesUrl && (
                  <Tooltip title="View in Jira">
                    <IconButton
                      size="small"
                      component="a"
                      href={finalComplianceIssuesUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        ml: 'auto',
                        flexShrink: 0,
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                      aria-label="View Compliance Issues in Jira"
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: 'info.main' }}>
                  <BugReport />
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                  {filteredStabilizationBugs.length}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Stabilization Bugs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {codeFreezeDate && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Code Freeze Date: {codeFreezeDate}
          </Typography>
        </Box>
      )}

      <Grid
        container
        spacing={3}
        sx={{
          mt: 4,
          mx: 0,
          width: '100%',
        }}
      >
        {/* Status Distribution Pie Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Status Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusDistribution.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Category Distribution Bar Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Tickets by Category
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryDistribution}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                <YAxis stroke={theme.palette.text.secondary} />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                  }}
                />
                <Bar dataKey="value" fill={chartColors[0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Validation Issues Bar Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Validation Issues Summary
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={validationIssuesSummary}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                <YAxis stroke={theme.palette.text.secondary} />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                  }}
                />
                <Bar dataKey="value" fill={chartColors[2]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Stabilization Bugs by Product Scope */}
        {stabilizationBugsByScope.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Stabilization Bugs by Product X Tech Scope
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stabilizationBugsByScope}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      color: theme.palette.text.primary,
                    }}
                  />
                  <Bar dataKey="value" fill={chartColors[1]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}

        {/* Stabilization Bugs Timeline */}
        {stabilizationBugsTimeline.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Stabilization Bugs Timeline
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stabilizationBugsTimeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="date" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      color: theme.palette.text.primary,
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      color: theme.palette.text.primary,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke={chartColors[4]}
                    strokeWidth={2}
                    name="Bugs"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
